import { __awaiter, __generator } from "tslib";
import { getModule } from '@scalprum/core';
import { useContext, useMemo, useReducer, useEffect, useRef, useCallback } from 'react';
import { RemoteHookContext } from './remote-hook-provider';
// Note: Removed helper functions and inlined logic to avoid stale closure issues
// Hook that returns a manager object
export function useRemoteHookManager() {
    var _this = this;
    var context = useContext(RemoteHookContext);
    var subscribe = context.subscribe, updateState = context.updateState, getState = context.getState, registerHook = context.registerHook, updateArgs = context.updateArgs;
    var _a = useReducer(function (x) { return x + 1; }, 0), update = _a[0], forceUpdate = _a[1];
    var subscriptions = useRef([]);
    var addHook = useCallback(function (config) {
        // Follow useRemoteHook pattern: immediate subscription
        var _a = subscribe(forceUpdate), id = _a.id, unsubscribe = _a.unsubscribe;
        // Track mounted state for this specific hook
        var isMounted = true;
        // Set initial loading state immediately
        updateState(id, { loading: true, error: null });
        updateArgs(id, config.args || []);
        // Load hook asynchronously (like useRemoteHook)
        var loadHook = function () { return __awaiter(_this, void 0, void 0, function () {
            var hookFunction, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, getModule(config.scope, config.module, config.importName)];
                    case 1:
                        hookFunction = _a.sent();
                        // Only update if both hook and manager are still mounted
                        if (isMounted) {
                            updateState(id, { loading: false, error: null });
                            updateArgs(id, config.args || []); // Set args before registering hook
                            registerHook(id, hookFunction);
                        }
                        return [3 /*break*/, 3];
                    case 2:
                        error_1 = _a.sent();
                        if (isMounted) {
                            console.error('Error loading hook:', error_1);
                            updateState(id, { loading: false, error: error_1 });
                        }
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); };
        loadHook();
        // Store subscription for cleanup
        var subscription = { id: id, unsubscribe: unsubscribe };
        subscriptions.current.push(subscription);
        // Return handle with fresh closures
        return {
            remove: function () {
                isMounted = false; // Mark as unmounted like useRemoteHook
                unsubscribe();
                // Remove from tracking array
                var index = subscriptions.current.indexOf(subscription);
                if (index !== -1) {
                    subscriptions.current.splice(index, 1);
                }
            },
            updateArgs: function (args) {
                console.log('Updating args for hook ID:', id, args, { isMounted: isMounted });
                if (isMounted) {
                    updateArgs(id, args);
                }
            },
        };
    }, []);
    var cleanup = useCallback(function () {
        // Clean up all subscriptions
        subscriptions.current.forEach(function (_a) {
            var unsubscribe = _a.unsubscribe;
            unsubscribe();
        });
        subscriptions.current.length = 0;
        forceUpdate();
    }, []);
    var hookResults = useMemo(function () {
        var results = subscriptions.current.map(function (_a) {
            var id = _a.id;
            var state = getState(id) || { loading: true, error: null };
            return {
                id: id,
                loading: state.loading,
                error: state.error,
                hookResult: state.hookResult,
            };
        });
        return results;
    }, [update]);
    // Cleanup on unmount
    useEffect(function () {
        return function () { return cleanup(); };
    }, [cleanup]);
    return {
        addHook: addHook,
        cleanup: cleanup,
        hookResults: hookResults,
    };
}
//# sourceMappingURL=use-remote-hook-manager.js.map